import { useState } from "react";
import { useSSE } from "@/lib/sse/useSSE";
import { useGraphState } from "@/lib/graph/useGraphState";
import { HUD } from "@/components/scene/HUD";
import { SceneRouter } from "@/components/scene/SceneRouter";
import { AgentLayer } from "@/components/scene/AgentLayer";
import { PresenterChrome } from "@/components/scene/PresenterChrome";
import { IdleHint } from "@/components/scene/IdleHint";
import { useAudioCues } from "@/lib/audio/useCues";
import { SignDocProvider } from "@/lib/sign/SignDocContext";
import { UserActionsProvider } from "@/lib/userActions/UserActionsContext";
import { AudienceLensProvider } from "@/lib/audienceLens/AudienceLensContext";
import { Thesis } from "@/components/scenes/act0/Thesis";
import { EventLogOverlay } from "@/components/scene/EventLogOverlay";
import { BuildVersionChip } from "@/components/scene/BuildVersionChip";

/**
 * Vite-spike App shell — mirrors frontend/app/demo/page.tsx exactly.
 * Difference: no "use client" directive (Vite is client-only by
 * definition), no Next file-based-routing wrapper. Component imports
 * resolve through the @ alias straight to the production frontend tree.
 *
 * If this file ends up structurally identical to the Next version, the
 * migration path is mostly mechanical: codemod the next/* imports, swap
 * the file-based router for react-router, replace /api server routes
 * with either a Node sidecar or the Go backend.
 */
export function App() {
  const ev = useSSE("exporter");
  const graph = useGraphState(ev.events);
  const [perspectiveOverride, setPerspectiveOverride] = useState<string | null>(null);

  useAudioCues(ev.events);

  const idle = ev.events.length === 0;

  return (
    <AudienceLensProvider>
      <UserActionsProvider>
        <SignDocProvider>
          <main className="relative flex h-screen min-h-0 flex-col gap-4 px-6 py-5 text-orbitra-paper">
            <HUD ev={ev} />
            <div className="min-h-0 flex-1">
              {idle ? <Thesis /> : <SceneRouter ev={ev} graph={graph} perspectiveOverride={perspectiveOverride} />}
            </div>

            <AgentLayer lines={ev.agentLines} />
            <IdleHint active={idle} />
            <PresenterChrome ev={ev} onPerspectiveOverride={setPerspectiveOverride} />
            <EventLogOverlay ev={ev} />
            <BuildVersionChip />
          </main>
        </SignDocProvider>
      </UserActionsProvider>
    </AudienceLensProvider>
  );
}
